module Meevo.Plugins.Register {

    export interface IRegisterPastTransactionContext {
        pastTransactionDateTime: Date;
        pastTransactionContext: Meevo.Model.PastTransactionContext;
        selectedDrawer: Meevo.Model.Drawer;
        selectedPrinter: Meevo.Model.Device;
        selectedTerminal: Meevo.Model.MerchantTerminal;
        selectedMerchantAccount: Meevo.Model.MerchantAccount;
    }

    export class RegisterTicketService {
        private warningInProgress = false;

        private pastTransactionContext: IRegisterPastTransactionContext = undefined;
        public get isPastTransactionMode() { return !!this.pastTransactionContext; }
        public saleBeingRecalculated: Model.Sale;
        public get recalculateSaleInProgress() { return this.saleBeingRecalculated && this.saleBeingRecalculated.recalculateSaleInProgress };

        constructor(
            protected $q: angular.IQService,
            protected $translate: angular.translate.ITranslateService,
            protected $rootScope,
            protected dataService: Meevo.Components.BaseClasses.IDataService,
            protected modalService: Meevo.Components.Modal.IModalService,
            protected printService: Meevo.Components.Common.IPrintService,
            protected busyService: Meevo.Components.Busy.IBusyService
        ) {
        }

        public getPastTransactionContext(): IRegisterPastTransactionContext {
            return this.pastTransactionContext;
        }

        enterPastTransactionMode(registerContext: Meevo.Plugins.Register.RegisterContext, pastTransactionContext: Meevo.Model.PastTransactionContext) {
            if (this.isPastTransactionMode) {
                return;
            }

            if (!pastTransactionContext) {
                return;
            }

            // pastTransactionContext will be used to track we are in register past transaction mode
            // - it will contain past transaction date, drawer, drawer session
            // - initial state of fields that should be restored
            this.pastTransactionContext = {
                pastTransactionDateTime: pastTransactionContext.pastDateTime,
                pastTransactionContext: pastTransactionContext,

                selectedDrawer: angular.copy(registerContext.selectedDrawer),
                selectedPrinter: angular.copy(registerContext.selectedPrinter),
                selectedTerminal: angular.copy(registerContext.selectedTerminal),
                selectedMerchantAccount: angular.copy(registerContext.selectedMerchantAccount),
            };

            registerContext.sale.pastTransactionDateTime = pastTransactionContext.pastDateTime;

            if (pastTransactionContext) {
                registerContext.sale.pastTransactionDrawerId = pastTransactionContext.drawerId;
                registerContext.sale.pastTransactionDrawerSessionId = pastTransactionContext.drawerSessionId;

                this.busyService.start();

                this.$rootScope.$broadcast(Meevo.Components.Common.Messages.SET_PAST_TRANSACTION_DRAWER, { name: "register" }, {
                    drawerId: pastTransactionContext.drawerId,
                    drawerSessionId: pastTransactionContext.drawerSessionId,
                });
            }
        }

        exitPastTransactionMode(registerContext: Meevo.Plugins.Register.RegisterContext) {
            if (!this.isPastTransactionMode) {
                return;
            }

            registerContext.selectedDrawer = this.pastTransactionContext.selectedDrawer;
            registerContext.selectedPrinter = this.pastTransactionContext.selectedPrinter;
            registerContext.selectedTerminal = this.pastTransactionContext.selectedTerminal;
            registerContext.printService.setSelectedTerminal(registerContext.selectedTerminal);
            registerContext.selectedMerchantAccount =  this.pastTransactionContext.selectedMerchantAccount;
            registerContext.paymentCats = undefined;

            this.$rootScope.$broadcast(Meevo.Components.Common.Messages.SET_CURRENT_DRAWER, { name: "register" }, {
                drawerId: registerContext.selectedDrawer.id,
            });

            this.pastTransactionContext = undefined;
        }

        getPastTransactionIdPrefix(): string {
            if (!this.isPastTransactionMode) {
                return null;
            }

            var transactionIdPrefix = moment(this.pastTransactionContext.pastTransactionDateTime).format('YYYYMMDD');

            return `${transactionIdPrefix}****`;
        }

        isPastTransactionPaymentValid(paymentType: Model.PaymentTypeListItem) {
            if (!paymentType || !this.isPastTransactionMode) {
                return true;
            }

            switch (paymentType.paymentCategory) {
                case Model.PaymentCategoryEnum.CreditDebit:
                case Model.PaymentCategoryEnum.Cash:
                case Model.PaymentCategoryEnum.Check:
                //case Model.PaymentCategoryEnum.ClientAccount:
                case Model.PaymentCategoryEnum.Other:
                    return true;
            }

            return false;
        }

        private mergeSale(sale: Model.Sale, response: Model.Sale, defaultTipCalculationsDiscountedPrice: boolean) {
            sale.subTotal = response.subTotal;
            sale.total = response.total;
            sale.baseTax = response.baseTax;
            sale.baseTaxIncluded = response.baseTaxIncluded;
            sale.finalTax = response.finalTax;
            sale.finalTaxIncluded = response.finalTaxIncluded;
            sale.tax = response.tax;
            sale.taxIncluded = response.taxIncluded;
            sale.taxes = response.taxes;
            sale.totalDiscounts = response.totalDiscounts;
            sale.totalPrepaidTip = response.totalPrepaidTip;
            sale.containsAvailableAutomaticDiscounts = response.containsAvailableAutomaticDiscounts;
            sale.totalAmountRedeemed = response.totalAmountRedeemed;
            sale.totalPrepaidTipRedeemed = response.totalPrepaidTipRedeemed;
            sale.hasPurchasedItems = response.hasPurchasedItems;
            sale.automaticDiscountIdToBeApplied = null;
            sale.discountBarCodeToBeApplied = null;
            sale.isServiceTaxDeclined = response.isServiceTaxDeclined;
            sale.isProductTaxDeclined = response.isProductTaxDeclined;
            sale.tipAmount = response.tipAmount;
            sale.tipPercentage = response.tipPercentage;
            sale.useDiscountedPrice = (response.useDiscountedPrice === undefined)
                ? (sale.useDiscountedPrice === undefined ? defaultTipCalculationsDiscountedPrice : sale.useDiscountedPrice)
                : response.useDiscountedPrice;
            sale.tipLines = response.tipLines;
            sale.tip = response.tip;
            sale.tipRefund = response.tipRefund;
            sale.selfPayments = response.selfPayments;
            sale.pastTransactionDateTime = response.pastTransactionDateTime;
            sale.pastTransactionDrawerId = response.pastTransactionDrawerId;
            sale.pastTransactionDrawerSessionId = response.pastTransactionDrawerSessionId;
            sale.removedAutomaticDiscountIds = response.removedAutomaticDiscountIds;
            sale.clientAccountBalances = response.clientAccountBalances;
            sale.tipConvenienceFee = response.tipConvenienceFee;
            sale.tipConvenienceFeeRefund = response.tipConvenienceFeeRefund;
        }

        private mergeSaleLines(sale: Model.Sale, response: Model.Sale, registerContext: Meevo.Plugins.Register.RegisterContext) {
            response.saleLines.forEach((responseSaleLine: Model.SaleLine) =>
            {
                // re-use existing saleline only if it exists in previous sale
                let existingSaleLine = _(sale.saleLines).findWhere({ id: responseSaleLine.id });
                if (existingSaleLine) {
                    existingSaleLine.newQuantity = existingSaleLine.quantity = responseSaleLine.quantity;
                    existingSaleLine.finalItemPrice = responseSaleLine.finalItemPrice;
                    existingSaleLine.baseItemPrice = responseSaleLine.baseItemPrice;
                    existingSaleLine.basePrice = responseSaleLine.basePrice;
                    existingSaleLine.finalPrice = responseSaleLine.finalPrice;

                    existingSaleLine.redeemedPackages = responseSaleLine.redeemedPackages;
                    existingSaleLine.packageRedeemed = responseSaleLine.packageRedeemed;
                    existingSaleLine.packageRedemptionType = responseSaleLine.packageRedemptionType;
                    existingSaleLine.packageRedemptionPath = responseSaleLine.packageRedemptionPath;

                    existingSaleLine.discountLines = responseSaleLine.discountLines;
                    existingSaleLine.removedAutomaticDiscountIds = responseSaleLine.removedAutomaticDiscountIds;

                    existingSaleLine.item = responseSaleLine.item;

                    existingSaleLine.taxes = responseSaleLine.taxes;
                    existingSaleLine.originalTaxes = responseSaleLine.originalTaxes;

                    existingSaleLine.fullCommissionItemPrice = responseSaleLine.fullCommissionItemPrice;
                    existingSaleLine.fullCommissionPrice = responseSaleLine.fullCommissionPrice;

                    existingSaleLine.charges = responseSaleLine.charges;
                    existingSaleLine.originalCharges = responseSaleLine.originalCharges;

                    existingSaleLine.baseItemCharge = responseSaleLine.baseItemCharge;
                    existingSaleLine.finalItemCharge = responseSaleLine.finalItemCharge;
                    existingSaleLine.baseCharge = responseSaleLine.baseCharge;
                    existingSaleLine.finalCharge = responseSaleLine.finalCharge;

                    existingSaleLine.isRefund = responseSaleLine.isRefund;
                    existingSaleLine.clientId = responseSaleLine.clientId;
                    existingSaleLine.client = responseSaleLine.client;

                    jQuery.extend(true, existingSaleLine, responseSaleLine);
                } else {
                    sale.saleLines.push(responseSaleLine);
                }
            });

            registerContext.allUniqueServices = {};
            // Delete any sale lines that no longer exist.
            // Rebuild unique service ids list.
            for (let i = sale.saleLines.length - 1; i >= 0; i--) {
                let saleLine = sale.saleLines[i];
                if (!_.findWhere(response.saleLines, { id: saleLine.id })) {
                    sale.saleLines.splice(i, 1);
                    continue;
                }

                if (!registerContext.isSaleLineAService(saleLine)) {
                    continue;
                }

                let appointmentServiceId = (<ServiceSaleLineItem>saleLine.item).appointmentServiceId;
                if (!appointmentServiceId) {
                    continue;
                }
                registerContext.allUniqueServices[appointmentServiceId] = saleLine;
            }
        }

        private mergePaymentLines(sale: Model.Sale, response: any) {
            // find newly added pending payment lines that existed prior to recalc sale call
            const existingPendingPaymentLines = _(sale.paymentLines).filter((salePaymentLine: any) =>
                !salePaymentLine.id && // newly added
                !salePaymentLine.skipPreserve &&
                salePaymentLine.isPending &&
                !!salePaymentLine.tempId
            );

            // preserve existing pending payment line if it is not in response payment lines
            const preservePendingPaymentLines = _(existingPendingPaymentLines).filter(existingPaymentLine =>
                !_(response.paymentLines).any(responsePaymentLine =>
                    responsePaymentLine.isPending &&
                    responsePaymentLine.tempId == existingPaymentLine.tempId
                )
            );

            sale.paymentLines = [ ...preservePendingPaymentLines, ...response.paymentLines ];
        }

        recalculateSale(registerContext: Meevo.Plugins.Register.RegisterContext, keepAccordionOpen: boolean = false, selectSection: RegisterSectionTypeEnum = null, checkWarnings: boolean = false, sale?: Model.Sale,
            skipAutoAddExistingServices = false) {

            let deferred = this.$q.defer();
            let showAccumulatedNotes = false;
            sale = this.saleBeingRecalculated = sale || registerContext.sale;

            if (sale.saleStage === Model.SaleStageEnum.Initial) {
                registerContext.registerNotesService.resetVisitNotes();
            }

            const isHoldMerge = !!sale.mergeOnHoldId;

            let doRecalculateSale = () => {
                this.populateTipSaleLine(registerContext, sale);
                registerContext.paymentPlanService.updatePaymentPlanPreRecalcuate(registerContext, sale);

                sale.saleLines.forEach((saleLine: Model.SaleLine) => {
                    if (!keepAccordionOpen) {
                        saleLine.isExpanded = false;
                        saleLine.isDetailExpanded = false;
                    }
                });

                if (!keepAccordionOpen) {
                    // Without this, if you have line expanded, then add a new line and attempt to re-expand original line, get weird effect
                    $(".register-items").css("overflow", "hidden");
                    $("v-pane").css("overflow", "hidden");
                }

                sale.recalculateSaleInProgress = true;

                this.dataService.add("drawer", sale, new Guid(registerContext.selectedDrawer.id), "sale")
                    .then((response: any) => {
                        sale.recalculateSaleInProgress = true;

                        if (this.isResponseConfirmation(response)) {
                            this.processResponseConfirmation(registerContext, sale, response, keepAccordionOpen, selectSection, checkWarnings);
                        }
                        else if (this.isResponseContinuation(response)) {
                            return this.processResponseContinuation(registerContext, sale, response, keepAccordionOpen, selectSection, checkWarnings).then(() => {
                                sale.suppressWarnings = false;
                                deferred.resolve();
                            });
                        }
                        else if (response.isWarning) {
                            this.handleSaleWarning(registerContext, sale, response, keepAccordionOpen, selectSection, checkWarnings);
                        } else {

                            // Check if our requested merge on hold transaction results in zero sales lines.  This means the on hold was already removed and we must prompt the user
                            if (sale.mergeOnHoldId != undefined && !sale.voidHold && !response.holdLockedDisplayName && !response.onHoldIds[sale.mergeOnHoldId.toString()]) {
                                this.modalService.alert("MESSAGE_HOLD_SERVICES_REMOVED");
                            }
                            else if (Model.Guard.isSomething(sale.discountBarCodeToBeApplied) && response["isWarning"] == true) {
                                sale.discountBarCodeToBeApplied = null;
                                this.modalService.alert("MESSAGE_SCANNED_INVALID_DISCOUNT");
                                deferred.reject();
                                return;
                            }

                            let salePrevious = jQuery.extend(true, {}, sale);

                            this.mergeSale(sale, response, registerContext.defaultTipCalculationsDiscountedPrice);

                            if (sale.pastTransactionDateTime) {
                                this.enterPastTransactionMode(registerContext, {
                                    pastDateTime: sale.pastTransactionDateTime,
                                    drawerId: sale.pastTransactionDrawerId,
                                    drawerSessionId: sale.pastTransactionDrawerSessionId
                                });
                            }

                            if (sale.saleLines.length != response.saleLines.length) {
                                // something changed in the sale lines (split from server), need to reload the whole sale.
                                keepAccordionOpen = false;
                            }

                            this.mergeSaleLines(sale, response, registerContext);

                            registerContext.registerDiscountService.handleDiscountValidation(registerContext, response, keepAccordionOpen, selectSection, checkWarnings);

                            for (let saleLineIndex = sale.saleLines.length - 1; saleLineIndex >= 0; saleLineIndex--) {
                                let saleLine = sale.saleLines[saleLineIndex];

                                saleLine.newQuantity = saleLine.quantity;
                                saleLine.newFinalItemPrice = saleLine.isRefund
                                    ? Math.abs(registerContext.addNormalizedNumbers(saleLine.finalItemPrice || 0, saleLine.finalItemCharge))
                                    : Math.abs(saleLine.finalItemPrice || 0);

                                if (saleLine.saleType === Model.SalesTypeEnum.GiftCard || saleLine.saleType === Model.SalesTypeEnum.ReloadGiftCard ||
                                    saleLine.saleType === Model.SalesTypeEnum.BulkGiftCard) {

                                    const giftCardItem = saleLine.item as GiftCardSaleLineItem;
                                    if (giftCardItem && giftCardItem.clientGiftCard && giftCardItem.clientGiftCard.clientType === Model.ClientTypeEnum.AnonymousOther) {
                                        giftCardItem.clientGiftCard.client = registerContext.AnonymousGiftCardPurchasedFor;
                                    }
                                }

                                if (saleLine.isRefund && registerContext.isSaleLineServiceOrSwapFee(saleLine)) {
                                    registerContext.updateRefundLineLink(saleLine);
                                }

                                // reestablish empty guid on HOUSE employee list item
                                if (saleLine.employeeId === Guid.empty.toString() && saleLine.employee && !saleLine.employee.id) {
                                    saleLine.employee.id = Guid.empty.toString();
                                }
                            }

                            registerContext.queuePosDisplay(sale.saleLines);

                            this.mergePaymentLines(sale, response);

                            this.checkHoldPaymentReversal(registerContext, sale);
                            registerContext.paymentPlanService.updatePaymentPlan(registerContext, sale);

                            if (sale.mergeClientIds.length > 0 || sale.mergeOnHoldId) {
                                for (const saleLine of sale.saleLines) {
                                    if (saleLine.saleType === Model.SalesTypeEnum.PaymentSurcharge && !saleLine.isRefund) {
                                        continue;
                                    }
                                    if (saleLine.client
                                        && saleLine.client.clientMembershipId
                                        && saleLine.client.membershipNumber
                                        && saleLine.client.membershipId
                                        && saleLine.client.membershipStatus
                                        && saleLine.client.membershipIndicatorStatus) {
                                        saleLine.client["clientIdCheckedForMembership"] = saleLine.client.id;
                                    }
                                    registerContext.addToAllUniqueClients(saleLine.client, saleLine.clientType, saleLine.appointmentId);
                                    if (registerContext.isSaleLineAService(saleLine)) {
                                        const serviceSaleLine = <Model.ServiceSaleLineItem>saleLine.item;
                                        if (serviceSaleLine.appointmentServiceId &&
                                            !registerContext.allUniqueServices[serviceSaleLine.appointmentServiceId]) {
                                            registerContext.allUniqueServices[serviceSaleLine.appointmentServiceId] = saleLine;
                                        }
                                        serviceSaleLine.temp = _.clone(serviceSaleLine);
                                    }
                                }

                                this.promptForImplicitMerge(registerContext, sale, response)
                                    .then(() => {
                                        return this.checkHoldLocked(response, selectSection);
                                    })
                                    .then((newSelectSection: RegisterSectionTypeEnum) => {
                                        selectSection = newSelectSection;
                                        _(response.appointNotes).each((note) => { note.entityId = note.appointmentId });
                                        registerContext.registerNotesService.getAppointmentNotes(response.appointNotes);

                                        if (response.syncAppointmentUpdates === false) {
                                            return registerContext.showRebookCandidates().then(() => {
                                                return registerContext.getClientCombinedAfterActions(undefined, sale);
                                            });
                                        }
                                        if (response.noLinkMergeClientIds.length > 0) {
                                            return registerContext.clientContext.handleOnHoldNoLinkMerge(sale, response.noLinkMergeClientIds, skipAutoAddExistingServices);
                                        } else if (response.mergeClientIds.length > 0 && registerContext.payingClient) {
                                            return registerContext.clientContext.processClientMerge(salePrevious, response)
                                        }
                                        else {
                                            return registerContext.clientContext.promptForServicesRelatedToImplicitlyAddAppointmentServiceSaleLines(salePrevious, sale);
                                        }
                                    })
                                    .then(() => {
                                        return this.checkHoldAuthorizedPayment(sale, registerContext);
                                    })
                                    .then(() => {
                                        return registerContext.displayRefundMismatchMessage(response.hasInvalidExternalRefundTips, response.hasInvalidAuthorizationPaymentRequests);
                                    })
                                    .then(() => {
                                        return registerContext.checkSignaturePendingPayments();
                                    })
                                    .then(() => {
                                        sale.mergeClientIds = [];
                                        sale.noLinkMergeClientIds = [];
                                        sale.mergeOnHoldId = undefined;
                                        sale.changedClientIds = [];
                                        sale.voidHold = undefined;
                                        sale.holdLockedDisplayName = undefined;
                                        registerContext.selectFirstClient(selectSection);

                                        if (checkWarnings || Object.keys(response.onHoldIds).length > 0) {
                                            const clients = _.pluck(registerContext.allUniqueClients, 'client');
                                            registerContext.checkClientBirthday(clients);
                                            registerContext.checkClientRestrictedWarning(clients).then(() => {
                                                registerContext.checkClientMembershipCreditCardZipMissing(clients);
                                            });
                                        }
                                        else {
                                            deferred.resolve();
                                            return deferred.promise;
                                        }
                                    });
                            }
                            else {
                                showAccumulatedNotes = true;
                                if (response.noLinkMergeClientIds.length > 0) {
                                    return registerContext.clientContext.handleOnHoldNoLinkMerge(sale, response.noLinkMergeClientIds);
                                }

                                sale.saleLines.forEach((saleLine: Model.SaleLine) => {
                                    if (registerContext.isSaleLineAService(saleLine)) {
                                        var serviceSaleLine = <Model.ServiceSaleLineItem>saleLine.item;
                                        serviceSaleLine.temp = _.clone(serviceSaleLine);
                                    }
                                });

                                registerContext.selectFirstClient(selectSection);
                            }

                            if (Object.keys(response.onHoldIds).length > Object.keys(sale.onHoldIds).length) {
                                registerContext.explicitHoldAdded = true;
                                registerContext.saleNotesMerge(response.saleNotes, sale);
                            }
                            sale.onHoldIds = response.onHoldIds;

                            if (registerContext.allUniqueClients.length == 0 && sale.tipLines.length) {
                                registerContext.addUniqueClientsFromTipLines(sale.tipLines);
                            }
                        }
                        this.addMissingAssociatedClientSaleLine(sale, registerContext);
                        sale.surchargeTotal = response.surchargeTotal;
                        deferred.resolve();
                    })
                    .then(() => {
                        if (!registerContext.sale.tipConvenienceFee || !isHoldMerge || !registerContext.tippyService) {
                            return;
                        }

                        return registerContext.tippyService.checkModule().then(isEnabled => {
                            if (!isEnabled) {
                                registerContext.tippyService.updateTipConvenienceFee(registerContext.sale, registerContext, 0);
                                return;
                            }

                            return registerContext.tippyService.getTippyMode(registerContext.selectedDrawer.id).then(mode => {
                                if (mode !== Tippy.TippyMode.Kiosk) {
                                    registerContext.tippyService.updateTipConvenienceFee(registerContext.sale, registerContext, 0);
                                    return;
                                }
                            })
                        });
                    })
                    .then(() => {
                        registerContext.recalculateTip(sale);
                    })
                    .catch((response) => {
                        sale.voidHold = undefined;
                        sale.mergeOnHoldId = undefined;
                        this.resetExternalEmployeeTipCharges(sale);
                        this.checkRedeemedPackageUnavailable(response, sale);
                        this.checkRedeemedPackageHasEligibleVariants(registerContext, response, sale, keepAccordionOpen, selectSection, checkWarnings);

                        deferred.reject();
                    })
                    .finally(() => {
                        sale.recalculateSaleInProgress = false;
                        sale.saleStage = Model.SaleStageEnum.Initial;
                        registerContext.setHasRebookCandidates(sale);
                        if (registerContext.switchToPaymentPlanRefund) {
                            registerContext.switchToPaymentPlanRefund = false;
                            registerContext.isRefundMode = true;
                            registerContext.selectSection(RegisterSectionTypeEnum.PaymentPlan);
                        }
                        if (!sale.isTemp) {
                            registerContext.sale = sale;
                        }
                    });
            }

            registerContext.paymentPlanService.checkRefundPaymentPlanIds(registerContext, sale).then(() => {
                if (sale.isTippyKioskMode === undefined) {
                    registerContext.isTippyKioskMode().then((isTippyKioskMode) => {
                        sale.isTippyKioskMode = isTippyKioskMode;
                        doRecalculateSale();
                    });
                } else {
                    doRecalculateSale();
                }
            });

            return deferred.promise.then(() => {
                if (showAccumulatedNotes) {
                    return registerContext.showRebookCandidates().then(() => {
                        return registerContext.getClientCombinedAfterActions();
                    });
                }
            });
        }

        private checkRedeemedPackageHasEligibleVariants(registerContext: Meevo.Plugins.Register.RegisterContext, response, sale: Model.Sale, keepAccordionOpen, selectSection, checkWarnings) {
            const propName: keyof Model.SaleLineItem = "productUsage";
            let recalculateSale = false;
            for (let error of (response.data.responseStatus.errors || [])
                .filter(error => error.fieldName.toLowerCase() === String(propName).toLowerCase())) {
                let saleLine = sale.saleLines.find(saleLine => saleLine.id === error.customState.saleLineId)

                if (!saleLine) {
                    continue;
                }

                saleLine.packageRedemptionPath = null;
                saleLine.packageRedeemed = null;
                saleLine.item.productUsage = null;
                recalculateSale = true;
            }

            if (recalculateSale) {
                this.recalculateSale(registerContext, keepAccordionOpen, selectSection, checkWarnings, sale);
            }
        }

        private checkRedeemedPackageUnavailable(response, sale: Model.Sale) {
            const propName: keyof Model.SaleLine = "packageRedemptionPath";
            for (let error of (response.data.responseStatus.errors || [])
                .filter(error => error.fieldName.toLowerCase() === String(propName).toLowerCase())) {
                for (let responseSaleLine of error.customState.saleLines) {
                    let saleLine = sale.saleLines.find(saleLine => saleLine.id === responseSaleLine.id)
                    if (!saleLine) {
                        continue;
                    }
                    saleLine.packageRedemptionPath = responseSaleLine.packageRedemptionPath;
                    saleLine.packageRedeemed = null;
                }
            }
        }

        private resetExternalEmployeeTipCharges(sale: Model.Sale) {
            for(let saleLine of sale.saleLines.filter(saleLine => saleLine.isRefund && !!saleLine.externalTipTransactionId)) {
                saleLine.finalItemPrice = saleLine.originalFinalItemPrice;
            }
        }

        private displayNewSelfPaymentHoldsMessage(registerContext: Meevo.Plugins.Register.RegisterContext, sale, newSelfPaymentClientIds) {
            let clientNames = [];
            newSelfPaymentClientIds.forEach(clientId => {
                let selfPayment = _(sale.selfPayments).findWhere({ clientId: clientId });
                if (selfPayment && selfPayment.client) {
                    clientNames.push(registerContext.formatClientDisplayName(selfPayment.client.firstName, selfPayment.client.lastName));
                }
            });
            let messageKey = clientNames.length > 1 ? "MESSAGE_SELF_PAY_HOLDS_MERGE" : "MESSAGE_SELF_PAY_HOLD_MERGE";
            return this.modalService.alert(this.$translate.instant(messageKey, { clients: clientNames.join(", ") }),
                Meevo.Components.Modal.ModalTranslationModes.DoNotTranslate)
        }

        private promptForImplicitMerge(registerContext: Meevo.Plugins.Register.RegisterContext, sale: Model.Sale, newSale: Model.Sale): angular.IPromise<boolean> {
            sale = sale || registerContext.sale;

            let newOnHoldIds = _(Object.keys(newSale.onHoldIds)).filter(onHoldId => {
                return Object.keys(sale.onHoldIds).indexOf(onHoldId) == -1;
            });

            if (sale.mergeClientIds.length > 0) {
                let newSelfPaymentClientIds = _(sale.selfPayments).chain().
                    filter(selfPayment => newOnHoldIds.indexOf(selfPayment.onHoldId) !== -1).
                    uniq(selfPayment => selfPayment.clientId).
                    map(selfPayment => selfPayment.clientId).value();
                if (newSelfPaymentClientIds.length > 0) {
                    return this.displayNewSelfPaymentHoldsMessage(registerContext, sale, newSelfPaymentClientIds);
                }
                else if (newOnHoldIds.map(key => newSale.onHoldIds[key]).indexOf(Model.HoldTypeEnum.Register) >= 0) {
                    return this.modalService.alert("MESSAGE_CLIENT_RETURNED_FROM_HOLD");
                }
                else {
                    return this.$q.resolve(true);
                }
            } else {
                return this.$q.resolve(true);
            }
        }

        private isResponseConfirmation(response) {
            if (!response || !response.responseStatus || !response.responseStatus.meta) {
                return false;
            }

            const meta = response.responseStatus.meta;

            return meta["YesFlag"] && meta["NoFlag"];
        }

        private isResponseContinuation(response) {
            if (!response || !response.responseStatus || !response.responseStatus.meta) {
                return false;
            }

            const meta = response.responseStatus.meta;

            return meta["ContinueFlag"];
        }

        private handleSaleWarning(registerContext: Meevo.Plugins.Register.RegisterContext, sale: Model.Sale, response, keepAccordionOpen, selectSection, checkWarnings) {
            // When adding appointment from no show window, recalculate is called twice in a row so this prevents warning modal from showing up twice
            if (this.warningInProgress) {
                return;
            }
            this.warningInProgress = true;
            this.modalService.confirmHandler(response.responseStatus.message, Components.Modal.ModalTranslationModes.DoNotTranslate).
                then((response: boolean) => {
                    if (response) {
                        sale.suppressWarnings = true;
                        this.recalculateSale(keepAccordionOpen, selectSection, checkWarnings);
                    }
                    else {
                        for (let clientIndex = registerContext.allUniqueClients.length - 1; clientIndex >= 0; clientIndex--) {
                            if (sale.mergeClientIds.indexOf(registerContext.allUniqueClients[clientIndex].getId()) >= 0) {
                                registerContext.selectClient(registerContext.allUniqueClients[clientIndex], true, false);
                                registerContext.removeClientConfirmation();
                            }
                        }
                        sale.mergeClientIds = [];
                        sale.changedClientIds = [];
                    }
                })
                .finally(() => {
                    this.warningInProgress = false
                });
        }

        private checkHoldPaymentReversal(registerContext: Meevo.Plugins.Register.RegisterContext, sale) {
            let deferred = this.$q.defer();
            let paymentLines = _(sale.paymentLines).where({ reverse: true });
            if (paymentLines.length > 0) {
                let amountToReverse: number = 0;
                let offsetIds: string[] = [];
                paymentLines.forEach((pl) => {
                    amountToReverse = registerContext.addNormalizedNumbers(amountToReverse, pl.amount);
                    offsetIds.push((pl as Model.SaleCreditCardPayment).paymentRequestId);
                });
                registerContext.startReversePaymentRequest(amountToReverse, offsetIds).then((succeeded) => {
                    if (!succeeded) {
                        return;
                    }

                    for (let i = sale.paymentLines.length - 1; i >= 0; i--) {
                        if (paymentLines.indexOf(sale.paymentLines[i]) > -1) {
                            sale.paymentLines.splice(i, 1);
                        }
                    }
                    if (!registerContext.isDirty(true)) {
                        registerContext.clearSale(deferred, sale);
                    }
                });
            }
        }

        private checkHoldLocked(saleReponse: Model.Sale, selectSection: RegisterSectionTypeEnum): ng.IPromise<RegisterSectionTypeEnum> {
            if (saleReponse.holdLockedDisplayName) {
                return this.modalService.alert(this.$translate.instant("MESSAGE_HOLD_LOCKED",
                    { holdDisplayName: saleReponse.holdLockedDisplayName })).then(() => {
                        return saleReponse.mergeOnHoldId ? RegisterSectionTypeEnum.Client : selectSection;
                    })
            }
            return this.$q.resolve(selectSection);
        }

        private checkHoldAuthorizedPayment(sale: Model.Sale, registerContext): ng.IPromise<boolean> {
            if (registerContext.hasPaymentAuthorization(sale) || registerContext.hasProjectedRefund(sale)) {
                return this.modalService.info(this.$translate.instant("MESSAGE_RETURN_FROM_ON_HOLD_SALE_AUTHORIZED_PAYMENT")).then((response) => {
                    if (registerContext.hasPaymentAuthorization(sale)) {
                        registerContext.printVoidReceipts(sale);
                    }
                    sale.paymentLines = registerContext.getRemainingAuthorizationReversalPaymentLines(sale);
                    return response;
                });
            }
            return this.$q.resolve(true);
        }

        private formatValidationError(responseStatus): string {
            let message = null;

            if (responseStatus.errors && responseStatus.errors.length > 0) {
                if (responseStatus.errors.length === 1) {
                    let error = responseStatus.errors[0];
                    message = this.$translate.instant(error.message, error.customState);
                } else {
                    let messages = ['<ul style="margin:0">'];
                    for (let error of responseStatus.errors) {
                        messages.push("<li>", this.$translate.instant(error.message, error.customState), "</li>");
                    }
                    messages.push('</ul>');
                    message = messages.join('');
                }
            }
            else if (responseStatus.message) {
                message = this.$translate.instant(responseStatus.message);
            }

            return message;
        }

        private processResponseContinuation(registerContext: Meevo.Plugins.Register.RegisterContext, sale: Model.Sale, response, keepAccordionOpen: boolean = false, selectSection: RegisterSectionTypeEnum = null, checkWarnings: boolean = false) {

            const message = this.formatValidationError(response.responseStatus);
            return this.modalService.alert(message, Meevo.Components.Modal.ModalTranslationModes.DoNotTranslate, true).then(() => {
                sale.suppressWarnings = true;
                return this.recalculateSale(registerContext, keepAccordionOpen, selectSection, checkWarnings, sale);
            });
        }

        private processResponseConfirmation(registerContext: Meevo.Plugins.Register.RegisterContext, sale: Model.Sale, response, keepAccordionOpen: boolean = false, selectSection: RegisterSectionTypeEnum = null, checkWarnings: boolean = false) {
            const isRetryNeeded = (isYesResult: boolean) => () => {
                let propName : string = response.responseStatus.meta["YesFlag"];
                propName = `${propName.substring(0, 1).toLowerCase()}${propName.substring(1)}`;
                sale[propName] = isYesResult ? true : false;
                return true;
            };

            const getMultipleChoiceResponse = (dialogResponse: Meevo.Model.MessageBoxButtonResult) => {
                return response.responseStatus.meta["YesFlag"];
            };

            const onMultipleChoice = (isRetryNeededLambda, multipleChoiceResponse) => {
                if (isRetryNeededLambda()) {
                    this.recalculateSale(registerContext, keepAccordionOpen, selectSection, checkWarnings, sale);
                }
            };

            let customState = JSON.parse(response.responseStatus.meta["CustomState"] || "{}");
            const options: Meevo.Components.Modal.IModalOptions = {
                templateUrl: "app/plugins/AppointmentBook/views/Message.html",
                controller: "meevo.plugins.appointmentBook.MessageController",
                controllerAs: "vm",
                bindToController: true,
                multiple: true,
                skipHide: true,
                onComplete: undefined,
                locals: {
                    message: response.responseStatus.message,
                    config: {
                        buttonsType: customState["isAutoRedeemWithProductUsage"]
                            ? Meevo.Model.ButtonTypeEnum.ContinueCancel : Meevo.Model.ButtonTypeEnum.YesNo,
                        callback: (result) => {
                            const isRetryNeededLambda = isRetryNeeded(result === Meevo.Model.MessageBoxButtonResult.Yes);
                            onMultipleChoice(isRetryNeededLambda, getMultipleChoiceResponse(result));
                        }
                    }
                }
            }

            this.modalService.showCustom(options, Meevo.Components.Modal.ModalTranslationModes.Translate).then((result) => {
                if (result && result.callback) {
                    result.callback(result.result, result.callbackParam);
                };
            });
        }

        private populateTipSaleLine(registerContext: Meevo.Plugins.Register.RegisterContext, sale: Model.Sale) {
            sale = sale || registerContext.sale;

            if (sale.saleLines.length > 0) {
                sale.saleLines = _(sale.saleLines).reject(x => x.saleType === Model.SalesTypeEnum.AssociatedClient && !x.isRefund);
            }

            const saleLineCount = sale.saleLines.filter(function (item) {
                return item.saleType != Model.SalesTypeEnum.PaymentSurcharge;
            }).length;

            if (saleLineCount === 0 && sale.tipLines.length > 0) {
                if (registerContext.selectedClient || registerContext.selectedClientType) {
                    registerContext.addAssociatedClientSaleLine(registerContext.selectedClient, registerContext.selectedClientType, sale);
                }
                else if (registerContext.payingClient || registerContext.payingClientType) {
                    registerContext.addAssociatedClientSaleLine(registerContext.payingClient, registerContext.payingClientType, sale);
                }
            }
        }

        populateFromAppointmentServices(registerContext: Meevo.Plugins.Register.RegisterContext, checkOutServices: any[], checkOutDetails?: any[], apptEntityVersion?: number) {
            if (!checkOutServices) {
                return;
            }

            registerContext.isPopulatingTicketFromAppointmentServices = true;

            let newSale = angular.copy(registerContext.sale);

            const notes = registerContext.registerNotesService.getAppointmentNotesFromAppointmentServices(newSale, checkOutServices);

            // need to maintain order in allUniqueClients
            let allUniqueClientIndexes = [];
            for (var i = registerContext.allUniqueClients.length - 1; i >= 0; i--) {
                let rc = registerContext.allUniqueClients[i];
                let key = (rc.client ? rc.client.id : rc.clientType);
                allUniqueClientIndexes[key] = i;
            }

            let rclient: RegisterClient = null;

            // add add-ons as services underneath their parent service
            let checkOutServicesIncludingAddOns = [];
            let checkOutDetailsIncludingAddOns = [];
            _(checkOutServices).each((checkOutService: any, index: number) => {
                let checkOutDetail = checkOutDetails ? checkOutDetails[index] : null;
                checkOutServicesIncludingAddOns.push(checkOutService);
                checkOutDetailsIncludingAddOns.push(checkOutDetail);
                if (checkOutService.serviceAddOns && checkOutDetail && checkOutDetail.applyToAddOns !== false) {
                    _(checkOutService.serviceAddOns).each((addOn: any) => {
                        let serviceAddOn = this.addServiceAddOnAsService(addOn, checkOutService);
                        checkOutServicesIncludingAddOns.push(serviceAddOn);
                        checkOutDetailsIncludingAddOns.push(checkOutDetail);
                    });
                }
            });

            let defaultSection = RegisterSectionTypeEnum.Services;
            newSale.changedClientIds = [];

            let promises = [];

            // populate the smart receipt
            _(checkOutServicesIncludingAddOns).each((service: Model.AppointmentService, index: number) => {
                if (service.clientId && newSale.mergeClientIds.indexOf(service.clientId) == -1) {
                    newSale.mergeClientIds.push(service.clientId);
                }

                let checkOutDetail = checkOutDetailsIncludingAddOns ? checkOutDetailsIncludingAddOns[index] : null;

                if (checkOutDetail &&
                    checkOutDetail.type === Meevo.Model.PaymentSubCategoryEnum.DepositCharge) {
                    if (!!newSale.pastTransactionDateTime) {
                        return;
                    }
                    promises.push(this.addPaymentLineFromAppointmentBook(registerContext, newSale, service, checkOutDetail));
                    return;
                }

                let saleLine = this.addSaleLineFromAppointmentBook(registerContext, newSale, service, checkOutDetail, apptEntityVersion);

                if (!saleLine) {
                    return;
                }

                saleLine.isNew = true;

                let apptId = saleLine.appointmentId;
                let key = (saleLine.client ? saleLine.client.id : apptId + "_" + saleLine.clientType);

                if (!_.isNumber(allUniqueClientIndexes[key])) {
                    allUniqueClientIndexes[key] = registerContext.allUniqueClients.length;
                    registerContext.addToAllUniqueClients(saleLine.client, saleLine.clientType, apptId);
                    rclient = registerContext.allUniqueClients[allUniqueClientIndexes[key]];

                    // add client to be prompted for appointments today
                    newSale.changedClientIds.push({
                        newClient: {
                            clientId: Model.Guard.isSomething(saleLine.client) ? saleLine.client.id : undefined,
                            clientType: saleLine.clientType,
                            appointmentId: null
                        },
                        oldClient: null
                    });
                }
                else {
                    registerContext.allUniqueClients[allUniqueClientIndexes[key]].addRelatedAppointment(apptId);
                }

                if (saleLine.employee && saleLine.employee.id) {
                    registerContext.selectedEmployee = saleLine.employee;
                }

                if (!registerContext.selectedClientType && !registerContext.selectedClient && index == 0) {
                    // set selected client only when none is selected
                    registerContext.selectedClientAppointmentId = apptId;
                    registerContext.selectedClientType = saleLine.clientType;
                    registerContext.selectedClient = saleLine.client;
                }

                if (registerContext.isSaleLineAService(saleLine) && saleLine.employee && saleLine.employee.employeeEntityType == Model.EmployeeEntityTypeEnum.Student) {
                    if (registerContext.selectedTeacher) {
                        if (registerContext.selectedTeacher.id != Meevo.Guid.empty.toString()) {
                            saleLine.teacher = registerContext.selectedTeacher;
                            saleLine.teacherId = registerContext.selectedTeacher.id;
                        }
                    }
                    else {
                        saleLine.needsTeacherAssignment = true;
                    }
                }
            });

            if (_(newSale.saleLines).findWhere({ needsTeacherAssignment: true })) {
                defaultSection = RegisterSectionTypeEnum.Teacher;
            }

            if (!registerContext.selectedDrawer.id) {
                return null;
            }

            let selectSection = device.portrait() && !device.largeTablet()
                ? RegisterSectionTypeEnum.Ticket
                : defaultSection;

            registerContext.registerNotesService.getAppointmentNotes(notes);

            this.$q.all(promises).then(() => {
                this.recalculateSale(registerContext, false, selectSection, true, newSale, this.isPastTransactionMode);
            });

            return rclient;
        }

        private addServiceAddOnAsService(serviceAddOn, service): Model.AppointmentService {
            var newService: Model.AppointmentService = _.clone(service);
            //newService.id = serviceAddOn.appointmentServiceId;
            newService.serviceId = serviceAddOn.addOnServiceId;
            newService.price = serviceAddOn.price;
            newService.serviceDisplayName = serviceAddOn.serviceDisplayName;
            newService.serviceShortcut = serviceAddOn.serviceShortcut;
            newService.isAddOn = true;
            newService.doNotAllowTips = serviceAddOn.doNotAllowTips;

            return newService;
        }

        private addPaymentLineFromAppointmentBook(registerContext: Meevo.Plugins.Register.RegisterContext, sale: Model.Sale, service, paymentDetails: any) {
            let clientType: Meevo.Model.ClientTypeEnum = service["clientType"];

            let client: Model.ClientListItem = undefined;

            if (clientType === Model.ClientTypeEnum.Client) {
                client = {
                    id: service["clientId"],
                    firstName: service["clientFirstName"],
                    lastName: service["clientLastName"],
                    displayName: service["clientDisplayName"],
                    primaryPhoneNumber: service["clientPhoneNumber"],
                    isRestricted: service["clientIsRestricted"],
                    birthDay: service["clientBirthDay"],
                    birthMonth: service["clientBirthMonth"]
                };
            }

            let payment: Model.SalePayment;

            switch (paymentDetails.type) {
                case Meevo.Model.PaymentSubCategoryEnum.DepositCharge:
                    payment = {
                        __type: "Millennium.Meevo.Register.Contracts.Model.SalePaymentClientAccount, Millennium.Meevo.Register.Contracts.Model",
                        amount: paymentDetails.depositAmount,
                        paymentCategory: Meevo.Model.PaymentCategoryEnum.ClientAccount,
                        paymentSubCategory: Meevo.Model.PaymentSubCategoryEnum.DepositCharge,
                        clientId: client ? client.id : null,
                        clientDisplayName: client ? client.displayName : null,
                        appointmentServiceId: paymentDetails.appointmentServiceId,
                        serviceShortcut: paymentDetails.serviceShortcut,
                    } as Model.SaleClientAccountPayment;
                    break;
            }

            let defer = this.$q.defer();

            let continueExecution = () => {
                const paymentLineExists = _(sale.paymentLines).any((paymentLine: Model.SaleClientAccountPayment) =>
                    paymentLine.paymentCategory === Meevo.Model.PaymentCategoryEnum.ClientAccount &&
                    paymentLine.paymentSubCategory === Meevo.Model.PaymentSubCategoryEnum.DepositCharge &&
                    paymentLine.appointmentServiceId == paymentDetails.appointmentServiceId);

                if (paymentLineExists) {
                    defer.resolve();
                    return;
                }

                let criteria: any = {
                    paymentCategory: payment.paymentCategory,
                };

                if (payment.paymentSubCategory) {
                    criteria.paymentSubCategory = payment.paymentSubCategory;
                }

                let paymentType = _(registerContext.paymentTypes).findWhere(criteria);

                if (paymentType) {
                    payment.paymentTypeId = paymentType.id;

                    registerContext.addPayment(payment, false, sale);

                    defer.resolve();
                } else {
                    console.error(`Payment Type not found!`, registerContext.paymentTypes, criteria);
                    defer.reject();
                }
            };

            if (registerContext.paymentTypes && registerContext.paymentCats) {
                continueExecution();
            } else {
                registerContext.loadPaymentCategories().then(() => continueExecution());
            }

            return defer.promise;
        }

        private addMissingAssociatedClientSaleLine(sale: Model.Sale, registerContext: Meevo.Plugins.Register.RegisterContext) {
            if (sale.saleLines.length < 1 && sale.tipLines.length > 0) {
                // the API call removed all sale lines. Need to add associatedClient sale line for tip lines & recalc
                const firstTipLineClientIds = sale.tipLines[0].clientIds;
                if (!firstTipLineClientIds) {
                    return this.$q.resolve();
                }
                const clientId = _(firstTipLineClientIds.filter(clientId => !!clientId && clientId != Meevo.Guid.empty)).first();
                if (!clientId) {
                    return this.$q.resolve();
                }
                return this.dataService.getItem('/Client', clientId)
                    .then((client: Model.Client) =>  {
                        registerContext.addAssociatedClientSaleLine(client, Model.ClientTypeEnum.Client, sale);
                        registerContext.addToAllUniqueClients(client, client.clientType, null);
                    });
            }

            return this.$q.resolve();
        }

        private addSaleLineFromAppointmentBook(registerContext: Meevo.Plugins.Register.RegisterContext, sale: Model.Sale, service, checkoutDetail?: any, apptEntityVersion?: number): Model.SaleLine {
            let noShowDetails = null;
            let cancellationDetails = null;
            let depositDetails = null;

            if (checkoutDetail) {
                switch (checkoutDetail.type) {
                    case Model.SalesTypeEnum.ServiceCancellationFee:
                        cancellationDetails = checkoutDetail;
                        break;
                    case Model.SalesTypeEnum.ServiceNoShowFee:
                        noShowDetails = checkoutDetail;
                        break;
                    case Model.SalesTypeEnum.DepositCredit:
                        depositDetails = checkoutDetail;
                        break;
                }
            }

            let isAlreadyInSaleLines = false;

            let isAddOn = !!service["isAddOn"];
            if (isAddOn) {
                if (
                    (cancellationDetails && !cancellationDetails.applyToAddOns) ||
                    (noShowDetails && !noShowDetails.applyToAddOns)
                ) {
                    return null;
                }

                isAlreadyInSaleLines = _(sale.saleLines).any((saleLine: Model.SaleLine) =>
                    saleLine.saleType == Model.SalesTypeEnum.Service &&
                    saleLine.item.id == service.serviceId &&
                    (saleLine.item as ServiceSaleLineItem).addOnAppointmentServiceId == service.id
                );
            }

            if (depositDetails) {
                isAlreadyInSaleLines = _(sale.saleLines).any((saleLine: Model.SaleLine) =>
                    saleLine.saleType === Model.SalesTypeEnum.DepositCredit &&
                    saleLine.isRefund &&
                    (saleLine.item as ClientAccountSaleLineItem).appointmentServiceId == service.id
                );
            }

            if (
                (!isAddOn && !depositDetails && !registerContext.allUniqueServices[service["id"]]) ||
                ((isAddOn || depositDetails) && !isAlreadyInSaleLines)
            ) {
                let saleLine: Model.SaleLine = {
                    employee: _.clone(service["employee"]),
                    employeeId: service["employeeId"],
                    item: new Meevo.Plugins.Register.ServiceSaleLineItem({
                        id: service["serviceId"],
                        displayName: service["serviceDisplayName"],
                        shortcut: service["serviceShortcut"],
                        appointmentServiceId: isAddOn ? null : service["id"],
                        appointmentId: isAddOn ? null : service["appointmentId"],
                        appointmentServiceDate: service["startDateTime"],
                        addOnAppointmentServiceId: isAddOn ? service["id"] : null,
                        addOnServiceId: isAddOn ? service["serviceId"] : null,
                        isAddOn: isAddOn,
                        cancellationTypeId: null,
                        appointmentEntityVersion: apptEntityVersion ? apptEntityVersion : null,
                        hasCheckoutCustomControls: service["hasCheckoutCustomControls"],
                        doNotAllowTips: service["doNotAllowTips"],
                        isRebooked: service["reBookCount"] || (service["associatedSales"] && service["associatedSales"].length > 0),
                        depositAmount: service["depositAmount"],
                        employeeId: service["employeeId"],
                        clientId: null,
                        clientType: service["clientType"],
                    }),
                    quantity: 1,
                    clientId: null,
                    clientType: service["clientType"],
                    client: null,
                    appointmentId: service["appointmentId"],  //Need Appoint Id for checking out Anonymous Clients with Services and AddOns, to avoid duplicate Client Names in smart receipt. #27148
                    saleType: Model.SalesTypeEnum.Service,
                    isNew: (registerContext.selectedClientType === Model.ClientTypeEnum.Client),
                    isAddOn: isAddOn,
                    isRefund: service["isRefund"],
                    showPoleMessage: Model.SaleLineStatusEnum.Added
                };

                if (cancellationDetails) {
                    saleLine.saleType = cancellationDetails.type;

                    let saleLineItem = <Meevo.Plugins.Register.ServiceSaleLineItem>saleLine.item;
                    saleLineItem.cancellationTypeId = cancellationDetails.id;
                }

                if (noShowDetails) {
                    saleLine.saleType = noShowDetails.type;
                }

                if (saleLine.clientType === Model.ClientTypeEnum.Client) {
                    saleLine.clientId = service["clientId"];
                    (<ServiceSaleLineItem>saleLine.item).clientId = service["clientId"];
                    let clientObject: Model.ClientListItem = {
                        id: service["clientId"],
                        firstName: service["clientFirstName"],
                        lastName: service["clientLastName"],
                        displayName: service["clientDisplayName"],
                        primaryPhoneNumber: service["clientPhoneNumber"],
                        isRestricted: service["clientIsRestricted"],
                        birthDay: service["clientBirthDay"],
                        birthMonth: service["clientBirthMonth"]
                    };
                    saleLine.client = clientObject;
                }

                if (!service["employee"]) {
                    saleLine.employee = {
                        id: service["employeeId"],
                        displayName: service["employeeDisplayName"],
                        firstName: service["employeeFirstName"],
                        lastName: service["employeeLastName"],
                        code: service["employeeCode"],
                        gender: service["employeeGender"],
                        allowTips: service["employeeAllowTips"],
                        allowSales: service["employeeAllowSales"],
                        employeeEntityType: service["employeeEntityType"],
                        nickName: service["employeeNickName"]
                    };
                }

                if (saleLine.clientType === Model.ClientTypeEnum.Client) {
                    var balanceInfo = sale.clientAccountBalances.find(balanceInfo => balanceInfo.clientId === saleLine.clientId);
                    saleLine.client.accountBalance = balanceInfo.accountBalance;
                        saleLine.client.allowChargesCreditOnAccount = balanceInfo.allowChargesCreditOnAccount;
                        if (depositDetails) {
                            let saleLineItem = <Meevo.Plugins.Register.ClientAccountSaleLineItem>saleLine.item;
                            saleLineItem.originalBalance = +balanceInfo.accountBalance;
                        }
                }


                if (depositDetails) {
                    saleLine.saleType = depositDetails.type;

                    let displayName = this.$translate.instant("LABEL_DEPOSIT_CREDIT_FOR", {
                        serviceShortcut: service['serviceShortcut'],
                        clientDisplayName: saleLine.client.displayName,
                    });

                    saleLine.item = new Meevo.Plugins.Register.ClientAccountSaleLineItem({
                        id: saleLine.clientId,
                        client: saleLine.client,
                        clientId: saleLine.clientId,
                        originalBalance: 0,
                        appointmentServiceId: depositDetails.appointmentServiceId,
                        displayName: displayName,
                    });

                    saleLine.quantity = 1;
                    saleLine.employee = registerContext.HouseEmployee;
                    saleLine.employeeId = registerContext.HouseEmployee.id;
                    saleLine.basePrice = saleLine.baseItemPrice = -Math.abs(depositDetails.depositAmount);
                    saleLine.finalPrice = saleLine.finalItemPrice = -Math.abs(depositDetails.depositAmount);
                    saleLine.isRefund = true;
                    saleLine.refundLocationId = depositDetails.depositLocationId;
                    saleLine.refundSaleDate = depositDetails.depositSaleDate;
                    saleLine.refundSaleId = depositDetails.depositSaleId;
                    saleLine.refundSaleLineId = depositDetails.depositSaleLineId;
                }

                // apply discounts
                if (cancellationDetails || noShowDetails) {
                    let discountLine: Model.DiscountLine = {
                        isStackable: true,
                        isAutoApply: false,
                        isBuyGet: false
                    };

                    if (cancellationDetails) {
                        discountLine.saleLineDiscountType = Meevo.Model.SaleLineDiscountTypeEnum.CancellationFee;
                        discountLine.entityId = cancellationDetails.id;
                        if (cancellationDetails.isPercent) {
                            discountLine.type = Model.DiscountTypeEnum.PercentOff;
                            discountLine.discountValue = registerContext.subtractNormalizedNumbers(100, (cancellationDetails.cancellationAmount * 100)) / 100;
                        }
                        else {
                            discountLine.type = Model.DiscountTypeEnum.SpecialPrice;
                            discountLine.discountValue = cancellationDetails.cancellationAmount;
                        }
                    }
                    else {
                        saleLine.saleType = Model.SalesTypeEnum.ServiceNoShowFee;
                        discountLine.saleLineDiscountType = Meevo.Model.SaleLineDiscountTypeEnum.NoShowFee;
                        discountLine.type = Model.DiscountTypeEnum.PercentOff;
                        discountLine.discountValue = registerContext.subtractNormalizedNumbers(100, (noShowDetails.fee * 100)) / 100;
                    }
                    saleLine.discountLines = [discountLine];
                }

                if (registerContext.isSaleLineAService(saleLine)) {
                    registerContext.allUniqueServices[service["id"]] = saleLine;
                }

                sale.saleLines.push(saleLine);

                return saleLine;
            }

            return null;
        }

        static factory(): Function {
            let svc = (
                $q,
                $translate,
                $rootScope,
                dataService,
                modalService,
                printService,
                busyService
            ) => new RegisterTicketService(
                $q,
                $translate,
                $rootScope,
                dataService,
                modalService,
                printService,
                busyService
            );

            svc.$inject = [
                "$q",
                "$translate",
                "$rootScope",
                "meevo.components.common.DataService",
                "meevo.components.modal.ModalService",
                "meevo.components.common.PrintService",
                "meevo.components.busy.BusyService",
            ];

            return svc;
        }
    }

    angular.module("meevo.plugins.register")
        .service("meevo.plugins.register.RegisterTicketService", RegisterTicketService.factory());
}